import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram } from 'lucide-react';
import { useConfig } from '../contexts/ConfigContext';

const Footer = () => {
  const { config } = useConfig();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-800 text-white">
      <div className="container-custom py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* School Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {config?.site?.name || 'Guidance Center'}
            </h3>
            <p className="text-gray-300 mb-4">
              {config?.site?.schoolName || 'Your School Name'}
            </p>
            <p className="text-gray-300 text-sm">
              Empowering students to achieve their full potential through comprehensive 
              guidance services and support.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/services" className="text-gray-300 hover:text-white transition-colors">
                  Our Services
                </Link>
              </li>
              <li>
                <Link to="/admission" className="text-gray-300 hover:text-white transition-colors">
                  Admission
                </Link>
              </li>
              <li>
                <Link to="/forms" className="text-gray-300 hover:text-white transition-colors">
                  Forms
                </Link>
              </li>
              <li>
                <Link to="/staff" className="text-gray-300 hover:text-white transition-colors">
                  Our Staff
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Services</h3>
            <ul className="space-y-2 text-gray-300">
              <li>Academic Counseling</li>
              <li>Career Guidance</li>
              <li>Personal Counseling</li>
              <li>Crisis Intervention</li>
              <li>Testing & Assessment</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-3 text-gray-400" />
                <span className="text-gray-300">
                  {config?.site?.phone || '(555) 123-4567'}
                </span>
              </div>
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-3 text-gray-400" />
                <span className="text-gray-300">
                  {config?.site?.email || 'slmcs.gcs@gmail.com'}
                </span>
              </div>
              <div className="flex items-start">
                <MapPin className="w-4 h-4 mr-3 text-gray-400 mt-1" />
                <div className="text-gray-300">
                  <p>{config?.site?.address?.street || 'Burgos Street, Talisay'}</p>
                  <p>
                    {config?.site?.address?.zip || '4700'}{' '}
                    {config?.site?.address?.city || 'Sorsogon'},{' '}
                    {config?.site?.address?.state || 'Sorsogon'}
                  </p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-6">
              <h4 className="font-medium mb-3">Follow Us</h4>
              <div className="flex space-x-4">
                <a 
                  href={config?.site?.social?.facebook || 'https://www.facebook.com/share/174So3d67f/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Follow us on Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                {config?.site?.social?.twitter && (
                  <a 
                    href={
                      config.site.social.twitter.startsWith('http')
                        ? config.site.social.twitter
                        : `https://twitter.com/${config.site.social.twitter.replace('@', '')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {config?.site?.social?.instagram && (
                  <a 
                    href={
                      config.site.social.instagram.startsWith('http')
                        ? config.site.social.instagram
                        : `https://instagram.com/${config.site.social.instagram.replace('@', '')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Office Hours */}
        <div className="border-t border-gray-700 mt-8 pt-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold mb-3">Office Hours</h4>
              <div className="text-gray-300 text-sm">
                {config?.site?.hours ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div>Monday - Friday:</div>
                    <div>{config.site.hours.monday}</div>
                    <div>Saturday:</div>
                    <div>{config.site.hours.saturday}</div>
                    <div>Sunday:</div>
                    <div>{config.site.hours.sunday}</div>
                  </div>
                ) : (
                  <div>
                    <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                    <p>Saturday: Closed</p>
                    <p>Sunday: Closed</p>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Emergency Contact</h4>
              <p className="text-gray-300 text-sm">
                For urgent matters outside office hours, please contact the main school office 
                or visit the nearest emergency room for immediate assistance.
              </p>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-700 mt-8 pt-8 text-center">
          <p className="text-gray-400 text-sm">
            © {currentYear} {config?.site?.schoolName || 'Your School Name'}. All rights reserved.
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Guidance Center Website - Empowering Student Success
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;